import { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

/**
 * Hook to track unsaved changes and show a confirmation dialog.
 * Returns: { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation }
 * 
 * Usage:
 *   const { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation } = useUnsavedChanges();
 *   // Set hasChanges to true when form is dirty
 *   // Call confirmNavigation(callback) before navigating away
 *   // Render <UnsavedDialog /> in JSX
 */
export function useUnsavedChanges() {
    const [hasChanges, setHasChanges] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    // Browser beforeunload warning
    useEffect(() => {
        const handler = (e) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasChanges]);

    const confirmNavigation = useCallback((action) => {
        if (hasChanges) {
            setPendingAction(() => action);
            setShowDialog(true);
        } else {
            action();
        }
    }, [hasChanges]);

    const handleLeave = useCallback(() => {
        setShowDialog(false);
        setHasChanges(false);
        if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }
    }, [pendingAction]);

    const handleStay = useCallback(() => {
        setShowDialog(false);
        setPendingAction(null);
    }, []);

    const UnsavedDialog = useCallback(() => (
        <Dialog open={showDialog} onOpenChange={handleStay}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-[#1B365D] text-xl">Modifications non enregistrées</DialogTitle>
                    <DialogDescription className="text-base mt-2">
                        Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={handleStay} className="border-gray-300" data-testid="stay-button">
                        Rester sur la page
                    </Button>
                    <Button onClick={handleLeave} className="bg-red-600 hover:bg-red-700 text-white" data-testid="leave-button">
                        Quitter sans enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    ), [showDialog, handleStay, handleLeave]);

    return { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation };
}
